(() => {
  'use strict';

  if (window.__spiritHeroMotionLoaded) return;
  window.__spiritHeroMotionLoaded = true;

  const ROOT_SELECTOR = '[data-spirit-hero], [data-spirit-category-rail-root]';
  const TAU = Math.PI * 2;
  const ORB_PROFILES = {
    trident: { sourceIndex: 0, color: '#f0b45e' },
    droplet: { sourceIndex: 1, color: '#a47cff' },
    spiral: { sourceIndex: 2, color: '#e58ac8' },
    waves: { sourceIndex: 3, color: '#5ea8e6' },
    tree: { sourceIndex: 4, color: '#5fd0ae' },
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const easeOut = (value) => 1 - Math.pow(1 - value, 3);
  const random = (min, max) => min + Math.random() * (max - min);

  class SpiritHeroMotion {
    constructor(root) {
      this.root = root;
      this.orbit = root.querySelector('[data-spirit-orbit]');
      this.centerElement = root.querySelector('[data-spirit-center]');
      this.fogCanvas = root.querySelector('.spirit-orbit__fog');
      this.lightCanvas = root.querySelector('.spirit-orbit__light');
      this.fogContext = this.fogCanvas?.getContext('2d') || null;
      this.lightContext = this.lightCanvas?.getContext('2d') || null;
      this.video = root.querySelector('.spirit-hero__video');
      this.rail = root.querySelector('[data-spirit-rail]');
      this.railTrack = root.querySelector('[data-spirit-rail-track]');
      this.railToggle = root.querySelector('[data-spirit-rail-toggle]');
      this.railToggleLabel = root.querySelector('[data-spirit-rail-toggle-label]');
      const configuredMotion = Number(root.dataset.motionStrength);
      const configuredRailSpeed = Number(root.dataset.railSpeed);
      this.motionStrength = clamp(Number.isFinite(configuredMotion) ? configuredMotion : 1, 0, 1);
      this.railSpeed = clamp(Number.isFinite(configuredRailSpeed) ? configuredRailSpeed : 12, 0, 60);
      this.pauseLabel = root.dataset.pauseLabel || 'Pause category movement';
      this.playLabel = root.dataset.playLabel || 'Play category movement';
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.reducedMotionQuery.matches;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.resizeObserver = null;
      this.intersectionObserver = null;
      this.animationFrame = 0;
      this.lastFrame = 0;
      this.frameDelta = 0;
      this.elapsed = 0;
      this.inView = true;
      this.documentVisible = !document.hidden;
      this.destroyed = false;
      this.dpr = 1;
      this.width = 0;
      this.height = 0;
      this.canvasOffsetX = 0;
      this.canvasOffsetY = 0;
      this.canvasWidth = 0;
      this.canvasHeight = 0;
      this.orbs = [];
      this.dust = [];
      this.mist = [];
      this.pulses = [];
      this.pulseTimer = 1.5;
      this.center = {
        x: 0,
        y: 0,
        baseX: 0,
        baseY: 0,
        focus: 0,
        tint: { r: 125, g: 78, b: 192 },
      };
      this.sprites = new Map();
      this.centerAuraSprite = null;
      this.originalCards = [];
      this.railCards = [];
      this.setWidth = 0;
      this.railOffset = 0;
      this.railAnimation = null;
      this.railAnimationDuration = 0;
      this.railPointerActive = false;
      this.railDragging = false;
      this.railSuppressClick = false;
      this.railPausedByUser = false;
      this.railResumeAt = 0;
      this.dragStartX = 0;
      this.dragStartOffset = 0;

      if (!this.orbit && !this.rail) return;

      if (this.orbit) {
        this.setupCenterCopy();
        this.setupOrbs();
        this.setupParticles();
      }
      this.setupRail();
      this.setupObservers();
      this.handleReducedMotion();
      this.resize();
      this.renderStaticState();
      this.start();
    }

    setupCenterCopy() {
      this.centerCopy = this.centerElement?.querySelector('[data-spirit-center-prompt]');
      this.centerCopyFallback = this.centerCopy?.querySelector('[data-spirit-center-fallback]');
      const prompt = this.centerCopy?.dataset.spiritCenterPrompt;
      if (!prompt || !this.centerCopyFallback) return;

      this.centerCopyContext = document.createElement('canvas').getContext('2d');
      if (!this.centerCopyContext) return;

      const characters = typeof Intl.Segmenter === 'function'
        ? Array.from(new Intl.Segmenter('he', { granularity: 'grapheme' }).segment(prompt), ({ segment }) => segment)
        : Array.from(prompt);
      const namespace = 'http://www.w3.org/2000/svg';
      const group = document.createElementNS(namespace, 'g');
      group.classList.add('spirit-orbit__center-glyphs');
      this.centerCopyGlyphs = characters.reverse().map((character) => {
        const element = document.createElementNS(namespace, 'text');
        element.textContent = character;
        element.setAttribute('text-anchor', 'start');
        element.setAttribute('direction', 'ltr');
        group.append(element);
        return { character, element };
      });
      this.centerCopy.append(group);

      document.fonts?.ready.then(() => this.layoutCenterCopy());
      document.fonts?.addEventListener('loadingdone', () => this.layoutCenterCopy(), { signal: this.signal });
    }

    layoutCenterCopy() {
      if (this.destroyed || !this.centerCopyGlyphs?.length) return;

      const style = getComputedStyle(this.centerCopyFallback);
      const fontSize = parseFloat(style.fontSize);
      const letterSpacing = parseFloat(style.letterSpacing) || 0;
      const context = this.centerCopyContext;
      context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      context.direction = 'ltr';
      const widths = this.centerCopyGlyphs.map(({ character }) => context.measureText(character).width);
      const length = widths.reduce((sum, width) => sum + width, 0) + letterSpacing * (widths.length - 1);
      if (!length) return;

      const radius = 122;
      const scale = Math.min(1, Math.PI * radius / length);
      let offset = -length * scale / 2;
      this.centerCopyGlyphs.forEach(({ element }, index) => {
        const width = widths[index] * scale;
        const angle = (offset + width / 2) / radius;
        const x = 220 + radius * Math.sin(angle);
        const y = 226 - radius * Math.cos(angle);
        element.style.fontSize = `${fontSize * scale}px`;
        element.setAttribute('x', String(-width / 2));
        element.setAttribute('y', '0');
        element.setAttribute('transform', `translate(${x} ${y}) rotate(${angle * 180 / Math.PI})`);
        offset += width + letterSpacing * scale;
      });
      this.centerCopy.classList.add('spirit-orbit__center-copy--positioned');
    }

    setupOrbs() {
      const elements = Array.from(this.root.querySelectorAll('[data-spirit-orb]'));

      this.orbs = elements.map((element, index) => {
        const key = element.dataset.spiritOrbKey || '';
        const profile = ORB_PROFILES[key] || {
          sourceIndex: index,
          color: '#a47cff',
        };
        const sourceIndex = profile.sourceIndex;
        const state = {
          element,
          index,
          key,
          sourceIndex,
          color: profile.color,
          seed: sourceIndex * 1.7 + 0.6,
          amp: 26 + (sourceIndex % 3) * 6,
          speedX: 0.42 + sourceIndex * 0.021,
          speedX2: 0.73 + sourceIndex * 0.029,
          speedY: 0.37 + sourceIndex * 0.019,
          speedY2: 0.67 + sourceIndex * 0.033,
          haloSeed: sourceIndex * 2.3 + 1.1,
          hover: false,
          focus: false,
          active: 0,
          x: 0,
          y: 0,
          baseX: 0,
          baseY: 0,
          driftX: 0,
          driftY: 0,
          controlX: 0,
          controlY: 0,
          flows: Array.from({ length: 5 }, () => ({
            progress: Math.random(),
            speed: random(0.05, 0.11),
            offset: random(-8, 8),
          })),
        };

        element.addEventListener(
          'pointerenter',
          () => {
            state.hover = true;
          },
          { signal: this.signal }
        );
        element.addEventListener(
          'pointerleave',
          () => {
            state.hover = false;
          },
          { signal: this.signal }
        );
        element.addEventListener(
          'focus',
          () => {
            state.focus = true;
          },
          { signal: this.signal }
        );
        element.addEventListener(
          'blur',
          () => {
            state.focus = false;
          },
          { signal: this.signal }
        );

        return state;
      });
    }

    setupParticles() {
      const dustCount = this.reducedMotion ? 26 : 64;
      const mistCount = this.reducedMotion ? 7 : 13;

      this.dust = Array.from({ length: dustCount }, (_, index) => ({
        x: random(-0.15, 1.15),
        y: random(-0.22, 1.22),
        radius: index % 9 === 0 ? random(2.2, 3.4) : random(0.5, 1.7),
        velocityX: random(-3, 3) / 1756,
        velocityY: random(-4, 1.4) / 767,
        twinkle: random(0, TAU),
        twinkleSpeed: random(0.35, 1.2),
        alpha: index % 9 === 0 ? random(0.16, 0.32) : random(0.12, 0.42),
        big: index % 9 === 0,
      }));

      this.mist = Array.from({ length: mistCount }, (_, index) => ({
        offsetX: random(-0.34, 0.34),
        offsetY: random(-0.3, 0.3),
        radius: random(0.1, 0.22),
        seed: index * 1.37 + 0.5,
        speed: random(0.045, 0.12),
        color: index % 3 === 0 ? '#c891ef' : '#ede7fd',
      }));
    }

    setupRail() {
      if (!this.rail || !this.railTrack) return;

      this.originalCards = Array.from(
        this.railTrack.querySelectorAll(':scope > [data-spirit-rail-card]')
      );
      if (!this.originalCards.length) return;

      this.ensureRailCopies();

      this.railTrack.addEventListener('spirit:catalog:updated', () => {
        this.railTrack.querySelectorAll('[data-spirit-rail-clone]').forEach(clone => clone.remove());
        this.originalCards = Array.from(this.railTrack.querySelectorAll(':scope > [data-spirit-rail-card]'));
        this.ensureRailCopies();
      }, { signal: this.signal });

      this.rail.addEventListener(
        'pointerdown',
        (event) => {
          if (event.button !== 0) return;
          this.railPointerActive = true;
          this.railDragging = false;
          this.dragStartX = event.clientX;
          this.dragStartOffset = this.getRailOffset();
        },
        { signal: this.signal }
      );

      this.rail.addEventListener(
        'pointermove',
        (event) => {
          if (!this.railPointerActive) return;
          const dragDistance = event.clientX - this.dragStartX;

          if (!this.railDragging) {
            if (Math.abs(dragDistance) < 6) return;
            this.railDragging = true;
            this.railResumeAt = Number.POSITIVE_INFINITY;
            this.railAnimation?.pause();
            this.rail.classList.add('is-dragging');
            try {
              this.rail.setPointerCapture(event.pointerId);
            } catch (_error) {
              // Pointer capture is an enhancement; dragging still works without it.
            }
          }

          event.preventDefault();
          this.setRailOffset(this.dragStartOffset + dragDistance);
        },
        { signal: this.signal }
      );

      const finishDrag = () => {
        if (!this.railPointerActive) return;
        const wasDragging = this.railDragging;
        this.railPointerActive = false;
        this.railDragging = false;
        this.rail.classList.remove('is-dragging');
        if (wasDragging) {
          this.railSuppressClick = true;
          this.railResumeAt = performance.now() + 1000;
          window.setTimeout(() => {
            this.railSuppressClick = false;
          }, 0);
        }
      };

      this.rail.addEventListener('pointerup', finishDrag, { signal: this.signal });
      this.rail.addEventListener('pointercancel', finishDrag, { signal: this.signal });
      this.rail.addEventListener('lostpointercapture', finishDrag, { signal: this.signal });
      this.rail.addEventListener(
        'click',
        (event) => {
          if (!this.railSuppressClick) return;
          event.preventDefault();
          event.stopPropagation();
        },
        { signal: this.signal }
      );

      this.railToggle?.addEventListener(
        'click',
        () => {
          this.railPausedByUser = !this.railPausedByUser;
          this.updateRailToggle();
        },
        { signal: this.signal }
      );

      this.updateRailToggle();
    }

    removeEditorAttributes(element) {
      Array.from(element.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-shopify-editor')) {
          element.removeAttribute(attribute.name);
        }
      });
    }

    ensureRailCopies() {
      if (!this.rail || !this.railTrack || !this.originalCards.length) return;

      const previousOffset = this.getRailOffset();
      const gap = parseFloat(getComputedStyle(this.railTrack).gap) || 16;
      const nextSetWidth = this.originalCards.reduce(
        (width, card) => width + card.offsetWidth + gap,
        0
      );
      const widthChanged = Math.abs(nextSetWidth - this.setWidth) > 0.5;
      this.setWidth = nextSetWidth;
      if (!this.setWidth) return;

      const neededSets = Math.max(2, Math.ceil(this.rail.clientWidth / this.setWidth) + 2);
      let existingSets = Math.round(this.railTrack.children.length / this.originalCards.length);

      while (existingSets < neededSets) {
        this.originalCards.forEach((original) => {
          const clone = original.cloneNode(true);
          clone.dataset.spiritRailClone = 'true';
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('role', 'presentation');
          clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
            element.setAttribute('tabindex', '-1');
          });
          this.removeEditorAttributes(clone);
          this.railTrack.appendChild(clone);
        });
        existingSets += 1;
      }

      this.railCards = Array.from(this.railTrack.children);
      if (widthChanged || (this.railSpeed > 0 && !this.railAnimation)) {
        this.setupRailAnimation(previousOffset);
      }
    }

    updateRailToggle() {
      if (!this.railToggle) return;
      const paused = this.railPausedByUser || this.reducedMotion;
      const label = paused ? this.playLabel : this.pauseLabel;
      this.railToggle.hidden = this.reducedMotion;
      this.railToggle.classList.toggle('is-paused', paused);
      this.railToggle.setAttribute('aria-pressed', String(paused));
      this.railToggle.setAttribute('aria-label', label);
      if (this.railToggleLabel) this.railToggleLabel.textContent = label;
      this.syncRailAnimationState();
    }

    normalizeRailOffset(offset) {
      if (!this.setWidth) return 0;
      let normalized = Number.isFinite(offset) ? offset : 0;
      while (normalized <= -this.setWidth) normalized += this.setWidth;
      while (normalized > 0) normalized -= this.setWidth;
      return normalized;
    }

    getRailOffset() {
      if (
        this.railAnimation &&
        this.railAnimationDuration > 0 &&
        Number.isFinite(Number(this.railAnimation.currentTime))
      ) {
        const currentTime = Number(this.railAnimation.currentTime);
        const elapsed = ((currentTime % this.railAnimationDuration) + this.railAnimationDuration) %
          this.railAnimationDuration;
        return -(elapsed / this.railAnimationDuration) * this.setWidth;
      }

      return this.normalizeRailOffset(this.railOffset);
    }

    setRailOffset(offset) {
      this.railOffset = this.normalizeRailOffset(offset);

      if (this.railAnimation && this.railAnimationDuration > 0) {
        const progress = this.setWidth ? -this.railOffset / this.setWidth : 0;
        this.railAnimation.currentTime = progress * this.railAnimationDuration;
        return;
      }

      this.applyRailTransform();
    }

    setupRailAnimation(offset = 0) {
      if (!this.railTrack || !this.setWidth) return;

      const startingOffset = this.normalizeRailOffset(offset);
      this.railAnimation?.cancel();
      this.railAnimation = null;
      this.railAnimationDuration = 0;

      if (this.railSpeed <= 0 || typeof this.railTrack.animate !== 'function') {
        this.railOffset = startingOffset;
        this.applyRailTransform();
        return;
      }

      this.railTrack.style.transform = '';
      this.railAnimationDuration = Math.max(1000, (this.setWidth / this.railSpeed) * 1000);
      this.railAnimation = this.railTrack.animate(
        [
          { transform: 'translate3d(0, 0, 0)' },
          { transform: `translate3d(${-this.setWidth}px, 0, 0)` },
        ],
        {
          duration: this.railAnimationDuration,
          iterations: Infinity,
          easing: 'linear',
        }
      );
      this.railAnimation.pause();
      this.setRailOffset(startingOffset);
      this.syncRailAnimationState();
    }

    syncRailAnimationState(now = performance.now()) {
      const shouldPlay =
        !this.reducedMotion &&
        !this.railPausedByUser &&
        !this.railDragging &&
        now >= this.railResumeAt &&
        this.inView &&
        this.documentVisible &&
        !this.destroyed;

      this.rail?.classList.toggle('is-motion-paused', !shouldPlay);
      if (!this.railAnimation) return;

      if (shouldPlay) {
        if (this.railAnimation.playState !== 'running') this.railAnimation.play();
      } else if (this.railAnimation.playState === 'running') {
        this.railAnimation.pause();
      }
    }

    setupObservers() {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.root);
      if (this.orbit) this.resizeObserver.observe(this.orbit);
      if (this.rail) this.resizeObserver.observe(this.rail);

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.inView = Boolean(entry?.isIntersecting);
          this.updateVideoPlayback();
          this.syncRailAnimationState();
        },
        { rootMargin: '200px 0px' }
      );
      this.intersectionObserver.observe(this.root);

      document.addEventListener(
        'visibilitychange',
        () => {
          this.documentVisible = !document.hidden;
          this.updateVideoPlayback();
          this.syncRailAnimationState();
        },
        { signal: this.signal }
      );

      this.reducedMotionQuery.addEventListener(
        'change',
        (event) => {
          this.reducedMotion = event.matches;
          this.handleReducedMotion();
        },
        { signal: this.signal }
      );
    }

    handleReducedMotion() {
      this.updateRailToggle();
      this.updateVideoPlayback();

      if (this.reducedMotion) {
        this.pulses = [];
        this.orbs.forEach((orb) => {
          orb.element.style.setProperty('--spirit-drift-x', '0px');
          orb.element.style.setProperty('--spirit-drift-y', '0px');
          orb.element.style.setProperty('--spirit-motion-scale', '1');
          orb.element.style.setProperty('--spirit-orb-focus', '0');
        });
        this.centerElement?.style.setProperty('--spirit-center-drift-x', '0px');
        this.centerElement?.style.setProperty('--spirit-center-drift-y', '0px');
        this.renderStaticState();
      }
    }

    updateVideoPlayback() {
      if (!this.video) return;
      const shouldPlay =
        !this.reducedMotion && this.inView && this.documentVisible && !this.destroyed;

      if (shouldPlay) {
        const playPromise = this.video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      } else {
        this.video.pause();
      }
    }

    resize() {
      if (this.destroyed) return;
      if (!this.orbit) {
        this.ensureRailCopies();
        return;
      }
      const rect = this.orbit.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      this.width = rect.width;
      this.height = rect.height;
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvasOffsetX = this.width * 0.14;
      this.canvasOffsetY = this.height * 0.26;
      this.canvasWidth = this.width * 1.28;
      this.canvasHeight = this.height * 1.52;

      [this.fogCanvas, this.lightCanvas].forEach((canvas) => {
        if (!canvas) return;
        const pixelWidth = Math.max(1, Math.round(this.canvasWidth * this.dpr));
        const pixelHeight = Math.max(1, Math.round(this.canvasHeight * this.dpr));
        if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
          canvas.width = pixelWidth;
          canvas.height = pixelHeight;
        }
      });

      this.updateGeometry();
      this.layoutCenterCopy();
      this.ensureRailCopies();
      if (this.reducedMotion) {
        this.updateRail(performance.now(), 0);
        this.draw();
      }
    }

    updateGeometry() {
      if (!this.orbit || !this.centerElement) return;
      const orbitRect = this.orbit.getBoundingClientRect();
      const centerRect = this.centerElement.getBoundingClientRect();

      this.center.baseX = centerRect.left - orbitRect.left + centerRect.width / 2;
      this.center.baseY = centerRect.top - orbitRect.top + centerRect.height / 2;
      this.center.x = this.center.baseX;
      this.center.y = this.center.baseY;

      this.orbs.forEach((orb) => {
        const rect = orb.element.getBoundingClientRect();
        orb.baseX = rect.left - orbitRect.left + rect.width / 2 - orb.driftX;
        orb.baseY = rect.top - orbitRect.top + rect.height / 2 - orb.driftY;
        orb.x = orb.baseX;
        orb.y = orb.baseY;
      });
    }

    start() {
      if (this.animationFrame || this.destroyed) return;
      this.lastFrame = performance.now();
      this.animationFrame = requestAnimationFrame((time) => this.frame(time));
    }

    frame(now) {
      if (this.destroyed) return;

      const delta = clamp((now - this.lastFrame) / 1000, 0, 0.05);
      this.frameDelta = delta;
      this.lastFrame = now;

      if (this.inView && this.documentVisible) {
        if (!this.reducedMotion) {
          this.elapsed += delta;
          if (this.orbit) this.updateOrbs(delta);
          this.updateRail(now, delta);
          if (this.orbit) this.draw();
        }
      }

      this.animationFrame = requestAnimationFrame((time) => this.frame(time));
    }

    updateOrbs(delta) {
      const motion = this.reducedMotion ? 0 : this.motionStrength;
      let focus = 0;
      let tintRed = 0;
      let tintGreen = 0;
      let tintBlue = 0;
      let tintWeight = 0;
      const sceneScale = this.width / 1756;

      this.orbs.forEach((orb) => {
        const target = orb.hover || orb.focus ? 1 : 0;
        orb.active = lerp(orb.active, target, clamp(delta * 9, 0, 1));
        const eased = easeOut(orb.active);
        const amplitude = orb.amp * motion * sceneScale;
        const dx =
          amplitude *
          (0.62 * Math.sin(this.elapsed * orb.speedX + orb.seed) +
            0.38 * Math.sin(this.elapsed * orb.speedX2 + orb.seed * 1.7));
        let dy =
          amplitude *
          (0.62 * Math.cos(this.elapsed * orb.speedY + orb.seed * 1.3) +
            0.38 * Math.sin(this.elapsed * orb.speedY2 + orb.seed * 2.1));
        if (window.innerWidth <= 820 && orb.key === 'droplet' && dy > 0) dy *= 0.22;

        orb.x = orb.baseX + dx;
        orb.y = orb.baseY + dy;
        orb.driftX = dx;
        orb.driftY = dy;
        focus = Math.max(focus, eased);
        const tint = this.hexToRgb(orb.color);
        const weight = orb.active + 0.0001;
        tintRed += tint.r * weight;
        tintGreen += tint.g * weight;
        tintBlue += tint.b * weight;
        tintWeight += weight;

        orb.element.style.setProperty('--spirit-drift-x', `${dx.toFixed(2)}px`);
        orb.element.style.setProperty('--spirit-drift-y', `${dy.toFixed(2)}px`);
        orb.element.style.setProperty('--spirit-motion-scale', (1 + 0.09 * eased).toFixed(4));
        orb.element.style.setProperty('--spirit-orb-focus', orb.active.toFixed(4));
        orb.element.style.setProperty('--h', orb.active.toFixed(3));
        orb.element.style.setProperty(
          '--spirit-disc-alpha',
          (0.42 + 0.16 * orb.active).toFixed(4)
        );
        orb.element.style.setProperty(
          '--spirit-ring-alpha',
          (0.34 + 0.5 * orb.active).toFixed(4)
        );
        orb.element.style.setProperty(
          '--spirit-label-alpha',
          (0.74 + 0.26 * orb.active).toFixed(4)
        );
        orb.element.style.setProperty(
          '--spirit-icon-glow-blur',
          `${(2 + 4 * orb.active).toFixed(2)}px`
        );
      });

      this.center.focus = focus;
      if (tintWeight > 0.01) {
        this.center.tint = {
          r: tintRed / tintWeight,
          g: tintGreen / tintWeight,
          b: tintBlue / tintWeight,
        };
      }
      const centerDriftX = motion ? 3 * Math.sin(this.elapsed * 0.4) * motion : 0;
      const centerDriftY = motion ? 3 * Math.cos(this.elapsed * 0.33) * motion : 0;
      this.center.x = this.center.baseX + centerDriftX;
      this.center.y = this.center.baseY + centerDriftY;
      const breath =
        this.reducedMotion || !motion
          ? 1
          : 1 + 0.05 * Math.sin(this.elapsed * 0.85) + 0.03 * focus;
      this.centerElement?.style.setProperty('--spirit-center-focus', focus.toFixed(4));
      this.centerElement?.style.setProperty(
        '--spirit-center-glow-blur',
        `${(10 + 20 * focus).toFixed(2)}px`
      );
      this.centerElement?.style.setProperty(
        '--spirit-center-glow-alpha',
        (0.4 + 0.4 * focus).toFixed(4)
      );
      this.centerElement?.style.setProperty('--spirit-center-breath', breath.toFixed(4));
      this.centerElement?.style.setProperty(
        '--spirit-center-drift-x',
        `${centerDriftX.toFixed(2)}px`
      );
      this.centerElement?.style.setProperty(
        '--spirit-center-drift-y',
        `${centerDriftY.toFixed(2)}px`
      );
    }

    updateRail(now, delta) {
      if (!this.railTrack || !this.setWidth) return;

      if (this.railAnimation) {
        this.syncRailAnimationState(now);
      } else {
        const canMove =
          !this.reducedMotion &&
          !this.railPausedByUser &&
          !this.railDragging &&
          now >= this.railResumeAt;

        if (canMove) {
          this.railOffset -= this.railSpeed * delta;
          this.applyRailTransform();
        }
      }

    }

    applyRailTransform() {
      if (!this.railTrack || !this.setWidth) return;
      this.railOffset = this.normalizeRailOffset(this.railOffset);
      this.railTrack.style.transform = `translate3d(${this.railOffset.toFixed(2)}px, 0, 0)`;
    }

    hexToRgb(color) {
      const value = String(color).replace('#', '');
      return {
        r: parseInt(value.slice(0, 2), 16),
        g: parseInt(value.slice(2, 4), 16),
        b: parseInt(value.slice(4, 6), 16),
      };
    }

    rgba(color, alpha) {
      const rgb = typeof color === 'string' ? this.hexToRgb(color) : color;
      return `rgba(${rgb.r | 0}, ${rgb.g | 0}, ${rgb.b | 0}, ${alpha})`;
    }

    mixRgb(from, to, amount) {
      return {
        r: from.r + (to.r - from.r) * amount,
        g: from.g + (to.g - from.g) * amount,
        b: from.b + (to.b - from.b) * amount,
      };
    }

    hslToRgb(hue, saturation = 0.75, lightness = 0.62) {
      const hueUnit = (((hue % 360) + 360) % 360) / 360;
      const amount = saturation * Math.min(lightness, 1 - lightness);
      const channel = (offset) => {
        const key = (offset + hueUnit * 12) % 12;
        return lightness - amount * Math.max(-1, Math.min(key - 3, 9 - key, 1));
      };
      return { r: channel(0) * 255, g: channel(8) * 255, b: channel(4) * 255 };
    }

    hueColor(hue) {
      const bucket = (Math.round((((hue % 360) + 360) % 360) / 12) * 12) % 360;
      const color = this.mixRgb(
        this.hslToRgb(bucket, 0.6, 0.62),
        { r: 232, g: 226, b: 250 },
        0.42
      );
      return `#${[color.r, color.g, color.b]
        .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
        .join('')}`;
    }

    makeSprite(color, soft = true) {
      const cacheKey = `${color}-${soft ? 'soft' : 'crisp'}`;
      if (this.sprites.has(cacheKey)) return this.sprites.get(cacheKey);
      const size = 128;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d');
      const gradient = context.createRadialGradient(
        size / 2,
        size / 2,
        0,
        size / 2,
        size / 2,
        size / 2
      );
      gradient.addColorStop(0, this.rgba(color, 1));
      gradient.addColorStop(soft ? 0.4 : 0.45, this.rgba(color, 0.5));
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      context.fillStyle = gradient;
      context.fillRect(0, 0, size, size);
      this.sprites.set(cacheKey, canvas);
      return canvas;
    }

    makeCenterAuraSprite() {
      if (this.centerAuraSprite) return this.centerAuraSprite;
      const size = 512;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d');
      const gradient = context.createConicGradient(0, size / 2, size / 2);
      [
        [0, '#3e7bff'],
        [0.13, '#6a57ff'],
        [0.26, '#9a4dff'],
        [0.4, '#d24bff'],
        [0.53, '#ff5cc0'],
        [0.66, '#ff6fa8'],
        [0.78, '#9a5cff'],
        [0.9, '#5a6bff'],
        [1, '#3e7bff'],
      ].forEach(([stop, color]) => gradient.addColorStop(stop, color));
      context.fillStyle = gradient;
      context.fillRect(0, 0, size, size);
      context.globalCompositeOperation = 'destination-in';
      const mask = context.createRadialGradient(
        size / 2,
        size / 2,
        0,
        size / 2,
        size / 2,
        size / 2
      );
      mask.addColorStop(0, 'rgba(0, 0, 0, 1)');
      mask.addColorStop(0.5, 'rgba(0, 0, 0, 0.95)');
      mask.addColorStop(1, 'rgba(0, 0, 0, 0)');
      context.fillStyle = mask;
      context.fillRect(0, 0, size, size);
      this.centerAuraSprite = canvas;
      return canvas;
    }

    drawSprite(context, color, x, y, radius, alpha) {
      if (!context || radius <= 0 || alpha <= 0.002) return;
      context.globalAlpha = clamp(alpha, 0, 1);
      context.drawImage(this.makeSprite(color), x - radius, y - radius, radius * 2, radius * 2);
      context.globalAlpha = 1;
    }

    quadraticPoint(fromX, fromY, controlX, controlY, toX, toY, amount) {
      const inverse = 1 - amount;
      return {
        x: inverse * inverse * fromX + 2 * inverse * amount * controlX + amount * amount * toX,
        y: inverse * inverse * fromY + 2 * inverse * amount * controlY + amount * amount * toY,
      };
    }

    drawConnection(context, orb) {
      const angle = Math.atan2(this.center.y - orb.y, this.center.x - orb.x);
      const normalX = Math.cos(angle + Math.PI / 2);
      const normalY = Math.sin(angle + Math.PI / 2);
      const wobble =
        (34 * Math.sin(this.elapsed * 0.4 + orb.seed) +
          18 * Math.sin(this.elapsed * 0.75 + orb.seed * 2.1)) *
        (0.5 + 0.5 * this.motionStrength) *
        (this.width / 1756);
      orb.controlX = (orb.x + this.center.x) / 2 + normalX * wobble;
      orb.controlY = (orb.y + this.center.y) / 2 + normalY * wobble;

      const sceneScale = this.width / 1756;
      const active = easeOut(orb.active);
      for (let index = 1; index <= 26; index += 1) {
        const amount = index / 26;
        const shape = Math.sin(amount * Math.PI);
        const wave =
          (Math.sin(amount * 8.5 - this.elapsed * 1.9 + orb.sourceIndex * 1.3) * 34 +
            Math.sin(amount * 3.2 - this.elapsed * 0.6 + orb.sourceIndex * 2.1) * 24) *
          sceneScale *
          (0.55 + 0.45 * this.motionStrength);
        const x = lerp(orb.x, this.center.x, amount) + normalX * wave * shape;
        const y = lerp(orb.y, this.center.y, amount) + normalY * wave * shape;
        const radius = (13 + 33 * shape) * sceneScale * (1 + 0.45 * active);
        const alpha = (0.056 + 0.105 * shape) * (0.75 + active);

        this.drawSprite(context, '#ede7fd', x, y, radius, alpha);
        if (active > 0.02) {
          this.drawSprite(context, orb.color, x, y, radius * 0.8, alpha * 0.55 * active);
        }
      }
    }

    drawFog() {
      const context = this.fogContext;
      if (!context) return;

      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, this.fogCanvas.width, this.fogCanvas.height);
      context.setTransform(
        this.dpr,
        0,
        0,
        this.dpr,
        this.canvasOffsetX * this.dpr,
        this.canvasOffsetY * this.dpr
      );
      context.globalCompositeOperation = 'source-over';

      const sceneScale = this.width / 1756;
      this.drawSprite(
        context,
        '#ede7fd',
        this.center.baseX + 16 * sceneScale * Math.sin(this.elapsed * 0.1) * this.motionStrength,
        this.center.baseY - 8 * sceneScale,
        250 * sceneScale * (1 + 0.05 * Math.sin(this.elapsed * 0.25)),
        0.12
      );

      const isMobile = window.innerWidth <= 820;
      const nebulaX = this.width * (isMobile ? 315 / 630 : 890 / 1756);
      const nebulaY = this.height * (isMobile ? 267 / 517 : 316 / 767);
      this.mist.forEach((mist) => {
        const driftX =
          70 * sceneScale * Math.sin(this.elapsed * mist.speed + mist.seed) * this.motionStrength;
        const driftY =
          40 * sceneScale * Math.cos(this.elapsed * mist.speed * 0.8 + mist.seed) * this.motionStrength;
        this.drawSprite(
          context,
          '#ede7fd',
          nebulaX + mist.offsetX * this.width + driftX,
          nebulaY + mist.offsetY * this.height + driftY,
          mist.radius * this.width * (1 + 0.16 * Math.sin(this.elapsed * 0.3 + mist.seed)),
          0.03
        );
      });

      this.orbs.forEach((orb) => this.drawConnection(context, orb));
    }

    drawDust(context, deltaScale = 0) {
      const displayScale = this.width / (window.innerWidth <= 820 ? 630 : 1756);
      this.dust.forEach((particle) => {
        if (!this.reducedMotion) {
          particle.x += particle.velocityX * deltaScale;
          particle.y += particle.velocityY * deltaScale;
          if (particle.y < -0.22) particle.y = 1.22;
          if (particle.y > 1.22) particle.y = -0.22;
          if (particle.x < -0.15) particle.x = 1.15;
          if (particle.x > 1.15) particle.x = -0.15;
        }
        const twinkle =
          0.45 + 0.55 * Math.sin(this.elapsed * particle.twinkleSpeed + particle.twinkle);
        const alpha = particle.alpha * (0.4 + 0.6 * twinkle);
        const x = particle.x * this.width;
        const y = particle.y * this.height;
        if (particle.big) {
          this.drawSprite(
            context,
            '#ffffff',
            x,
            y,
            particle.radius * 4.2 * displayScale,
            alpha * 0.5
          );
        }
        context.fillStyle = this.rgba({ r: 240, g: 232, b: 253 }, alpha);
        context.beginPath();
        context.arc(x, y, particle.radius * displayScale, 0, TAU);
        context.fill();
      });
    }

    drawCenterAura(context) {
      const focus = this.center.focus;
      const breath =
        1 + 0.18 * Math.sin(this.elapsed * 0.8) + 0.07 * Math.sin(this.elapsed * 1.9 + 1.3);
      const hueShift = 260 + 62 * Math.sin(this.elapsed * 0.17);
      const isMobile = window.innerWidth <= 820;
      const centerScale = isMobile
        ? (this.width / 630) * Math.sqrt(630 / 1756)
        : this.width / 1756;

      context.save();
      context.lineCap = 'round';
      for (let index = 0; index < 14; index += 1) {
        const angle = this.elapsed * 0.22 + (index * TAU) / 14;
        const flicker =
          0.35 +
          0.65 *
            Math.pow(0.5 + 0.5 * Math.sin(this.elapsed * 1.1 + index * 1.7), 2);
        const length =
          (210 + 46 * Math.sin(this.elapsed * 0.7)) *
          centerScale *
          breath *
          (1 + 0.25 * focus);
        const endX = this.center.x + Math.cos(angle) * length;
        const endY = this.center.y + Math.sin(angle) * length;
        const gradient = context.createLinearGradient(
          this.center.x,
          this.center.y,
          endX,
          endY
        );
        const rayColor = this.hslToRgb(hueShift + index * 5, 0.75, 0.62);
        gradient.addColorStop(0, this.rgba(rayColor, (0.05 + 0.06 * focus) * flicker));
        gradient.addColorStop(0.6, this.rgba(rayColor, (0.02 + 0.03 * focus) * flicker));
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
        context.strokeStyle = gradient;
        context.lineWidth = (7 + 14 * flicker) * centerScale;
        context.beginPath();
        context.moveTo(this.center.x, this.center.y);
        context.lineTo(endX, endY);
        context.stroke();
      }
      context.restore();

      const aura = this.makeCenterAuraSprite();
      const radius = 250 * centerScale * breath * (1 + 0.22 * focus);
      context.save();
      context.translate(this.center.x, this.center.y);
      context.rotate(this.elapsed * 0.13);
      context.globalAlpha = 0.52 + 0.12 * Math.sin(this.elapsed * 0.9) + 0.2 * focus;
      context.drawImage(aura, -radius, -radius, radius * 2, radius * 2);
      context.rotate(-this.elapsed * 0.34);
      context.globalAlpha = 0.34 + 0.14 * focus;
      const innerRadius = radius * 0.66;
      context.drawImage(aura, -innerRadius, -innerRadius, innerRadius * 2, innerRadius * 2);
      context.restore();

      for (let index = 0; index < 5; index += 1) {
        const angle = this.elapsed * (0.5 + 0.12 * index) + (index * TAU) / 5;
        const orbit = (62 + 24 * Math.sin(this.elapsed * 0.6 + index)) * centerScale * breath;
        this.drawSprite(
          context,
          this.hueColor(hueShift + index * 66),
          this.center.x + Math.cos(angle) * orbit,
          this.center.y + Math.sin(angle) * orbit * 0.85,
          (42 + 16 * Math.sin(this.elapsed + index)) * centerScale,
          0.14 + 0.06 * focus
        );
      }

      const coreColor = this.mixRgb(
        { r: 255, g: 255, b: 255 },
        this.hslToRgb(hueShift, 0.8, 0.62),
        0.4
      );
      const coreRadius =
        (46 + 11 * Math.sin(this.elapsed * 1.6)) *
        centerScale *
        breath *
        (1 + 0.25 * focus);
      this.drawSprite(
        context,
        `#${[coreColor.r, coreColor.g, coreColor.b]
          .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
          .join('')}`,
        this.center.x,
        this.center.y,
        coreRadius,
        0.5 + 0.22 * focus
      );
      this.drawSprite(context, '#ffffff', this.center.x, this.center.y, coreRadius * 0.44, 0.5 + 0.24 * focus);
    }

    drawFlows(context) {
      const displayScale = this.width / (window.innerWidth <= 820 ? 630 : 1756);
      this.orbs.forEach((orb) => {
        const angle = Math.atan2(this.center.y - orb.y, this.center.x - orb.x);
        const normalX = Math.cos(angle + Math.PI / 2);
        const normalY = Math.sin(angle + Math.PI / 2);

        orb.flows.forEach((flow) => {
          if (!this.reducedMotion) {
            flow.progress =
              (flow.progress +
                flow.speed *
                  this.frameDelta *
                  (0.6 + 1.6 * orb.active) *
                  (0.4 + 0.6 * this.motionStrength)) %
              1;
          }
          const point = this.quadraticPoint(
            orb.x,
            orb.y,
            orb.controlX,
            orb.controlY,
            this.center.x,
            this.center.y,
            flow.progress
          );
          const fade = Math.sin(flow.progress * Math.PI);
          const alpha = (0.11 + 0.5 * orb.active) * fade;
          const radius =
            (1.2 + 2.2 * orb.active) * (0.6 + 0.6 * fade) * displayScale;
          const x = point.x + normalX * flow.offset * (1 - flow.progress) * displayScale;
          const y = point.y + normalY * flow.offset * (1 - flow.progress) * displayScale;
          const mixedColor = this.mixRgb(
            this.hexToRgb(orb.color),
            { r: 255, g: 255, b: 255 },
            0.5
          );
          const paleColor = `#${[mixedColor.r, mixedColor.g, mixedColor.b]
            .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
            .join('')}`;
          this.drawSprite(context, paleColor, x, y, radius * 3.4, alpha);
          this.drawSprite(context, '#ffffff', x, y, radius * 1.3, alpha * 0.9);
        });
      });
    }

    drawPulses(context) {
      if (!this.reducedMotion) {
        this.pulseTimer -= this.frameDelta * (1 + 1.6 * this.center.focus);
        if (this.pulseTimer <= 0) {
          this.pulseTimer = 3.4;
          const displayScale = this.width / (window.innerWidth <= 820 ? 630 : 1756);
          this.pulses.push({ radius: 72 * 0.9 * displayScale, life: 1 });
        }
      }

      const pulseColor = this.mixRgb(
        { r: 150, g: 120, b: 232 },
        this.center.tint,
        this.center.focus * 0.5
      );
      const sceneScale = this.width / 1756;
      for (let index = this.pulses.length - 1; index >= 0; index -= 1) {
        const pulse = this.pulses[index];
        pulse.radius +=
          (42 + 70 * this.center.focus) * sceneScale * this.frameDelta;
        pulse.life -= this.frameDelta / 3.2;
        if (pulse.life <= 0) {
          this.pulses.splice(index, 1);
          continue;
        }
        const alpha =
          0.12 * easeOut(pulse.life) * (0.55 + 0.7 * this.center.focus);
        context.strokeStyle = this.rgba(pulseColor, alpha);
        context.lineWidth = (1.2 + 2.4 * pulse.life) * sceneScale;
        context.beginPath();
        context.arc(this.center.x, this.center.y, pulse.radius, 0, TAU);
        context.stroke();
      }
    }

    drawLight() {
      const context = this.lightContext;
      if (!context) return;

      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, this.lightCanvas.width, this.lightCanvas.height);
      context.setTransform(
        this.dpr,
        0,
        0,
        this.dpr,
        this.canvasOffsetX * this.dpr,
        this.canvasOffsetY * this.dpr
      );
      context.globalCompositeOperation = 'lighter';

      this.drawPulses(context);
      this.drawCenterAura(context);

      const displayScale = this.width / (window.innerWidth <= 820 ? 630 : 1756);
      this.orbs.forEach((orb) => {
        const radius = 64 * displayScale;
        const haloX =
          orb.x +
          10 * displayScale * Math.sin(this.elapsed * 0.5 + orb.haloSeed) * this.motionStrength;
        const haloY =
          orb.y +
          9 *
            displayScale *
            Math.cos(this.elapsed * 0.42 + orb.haloSeed * 1.4) *
            this.motionStrength;
        const baseRadius =
          radius * 2.15 * (1 + 0.08 * Math.sin(this.elapsed * 0.9 + orb.haloSeed));
        const hoverRadius = baseRadius * (1 + 0.55 * orb.active);
        const baseAlpha = 0.22 + 0.03 * Math.sin(this.elapsed * 1.1 + orb.haloSeed);
        const paleRgb = this.mixRgb(
          this.hexToRgb(orb.color),
          { r: 255, g: 255, b: 255 },
          0.5
        );
        const paleColor = `#${[paleRgb.r, paleRgb.g, paleRgb.b]
          .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
          .join('')}`;

        this.drawSprite(
          context,
          orb.color,
          haloX,
          haloY,
          hoverRadius,
          baseAlpha + 0.4 * orb.active
        );
        this.drawSprite(
          context,
          orb.color,
          orb.x,
          orb.y,
          radius * (2.5 + 0.5 * orb.active),
          0.24 + 0.34 * orb.active
        );
        this.drawSprite(
          context,
          paleColor,
          orb.x,
          orb.y,
          radius * (1.35 + 0.4 * orb.active),
          0.24 + 0.2 * orb.active
        );
        this.drawSprite(
          context,
          '#ffffff',
          orb.x,
          orb.y,
          radius * (0.56 + 0.05 * orb.active),
          0.05 + 0.05 * orb.active
        );

        for (let sparkleIndex = 0; sparkleIndex < 5; sparkleIndex += 1) {
          const angle =
            this.elapsed * (0.45 + 0.13 * sparkleIndex) +
            sparkleIndex * (TAU / 5) +
            orb.seed * 2;
          const sparkleOrbit =
            radius *
            (1.42 + 0.3 * Math.sin(this.elapsed * 0.8 + sparkleIndex + orb.seed)) *
            (1 + 0.22 * orb.active);
          const twinkle =
            0.35 +
            0.65 *
              (0.5 +
                0.5 *
                  Math.sin(
                    this.elapsed * 3 + sparkleIndex * 1.7 + orb.seed * 3
                  ));
          this.drawSprite(
            context,
            '#ffffff',
            orb.x + Math.cos(angle) * sparkleOrbit,
            orb.y + Math.sin(angle) * sparkleOrbit,
            (3.5 + 4 * twinkle + 5 * orb.active) * displayScale,
            (0.09 + 0.4 * orb.active) * twinkle
          );
        }
      });
      this.drawDust(context, this.frameDelta);
      context.globalCompositeOperation = 'source-over';
    }

    draw() {
      if (!this.width || !this.height) return;
      this.drawFog();
      this.drawLight();
    }

    renderStaticState() {
      if (!this.orbit) return;
      this.updateGeometry();
      this.updateOrbs(1);
      this.updateRail(performance.now(), 0);
      this.draw();
    }

    destroy() {
      this.destroyed = true;
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = 0;
      this.abortController.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.video?.pause();
      this.railAnimation?.cancel();
      this.railAnimation = null;

      this.railTrack
        ?.querySelectorAll('[data-spirit-rail-clone="true"]')
        .forEach((clone) => clone.remove());
      this.railCards = [];
      this.root.spiritHeroMotion = null;
    }
  }

  const initialize = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (root.spiritHeroMotion) return;
      root.spiritHeroMotion = new SpiritHeroMotion(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);
    root?.spiritHeroMotion?.destroy();
  });
})();
